import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner} from 'react-bootstrap';
import { IWebLink } from '../../../models/webLink';
import { useMutation, useQueryClient } from 'react-query';
import { updateWebLink } from '../../../api/agent';
import { useParams } from 'react-router-dom';
import { useOrgContext } from '../../../context/org';
import { FaLink, FaSave, FaTimes, FaExclamationTriangle } from 'react-icons/fa';
type Props = {
    webLink: IWebLink;
    setCurrWebLink: React.Dispatch<React.SetStateAction<IWebLink | undefined>>
}

const ViewWebLink = ({webLink, setCurrWebLink} : Props) => {
    const [text, setText] = useState(webLink.text);
    const [error, setError] = useState<string | null>(null);
    
    const handleClose = () => setCurrWebLink(undefined);

    const {orgId, agentId} = useParams();
    const {membership} = useOrgContext();
    const allowed = ["owner", "admin", "editor"]
    const permission = !allowed.includes(membership?.role || "");

    const queryClient = useQueryClient();
    const { mutateAsync: updateWebLinkFn, isLoading } = useMutation(
        updateWebLink, // mutation function goes here
        {
            // mutationKey: ['Agents', agent?.id],
            onSuccess: () => {
                // queryClient.invalidateQueries(['Agents', agentId]);
                queryClient.invalidateQueries(['WebLinks', agentId]);
                handleClose();
            },
            onError: (error) => {
                setError("Unexpected error. Please try again or contact admin.");
            },
        }
    );

    const handleUpdate = async () => {
        if(!agentId || !orgId) {
            setError("Invalid agent or organization ID");
            return;
        }
        updateWebLinkFn({
            id: webLink.id,
            text,
            orgId,
            agentId: agentId,
        })
    }

    return (
        <Modal 
            show={!!webLink} 
            onHide={handleClose} 
            size='lg'
            contentClassName="bg-dark border-secondary"
        >
            <Modal.Header 
                closeButton
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <div className="d-flex align-items-center">
                    <div 
                        className="me-3 d-flex align-items-center justify-content-center"
                        style={{
                            width: "32px",
                            height: "32px",
                            backgroundColor: "#17a2b8",
                            borderRadius: "8px"
                        }}
                    >
                        <FaLink className="text-white" size={16} />
                    </div>
                    <Modal.Title className="text-light mb-0" style={{ fontSize: "18px", fontWeight: "600" }}>
                        Edit Web Link
                    </Modal.Title>
                </div>
            </Modal.Header>
            
            <Modal.Body style={{ backgroundColor: "#1a1a1a" }}>
                <Form>
                    <Form.Group className="mb-4">
                        <Form.Label className="text-light fw-semibold mb-2">
                            URL
                        </Form.Label>
                        <Form.Text className="d-block mb-3" style={{ color: "#ccc" }}>
                            The source URL for this content.
                        </Form.Text>
                        <Form.Control
                            type='text'
                            value={webLink.url}
                            readOnly
                            className="bg-dark text-light border-secondary mb-4"
                            style={{ 
                                backgroundColor: "#2d2d2d",
                                borderColor: "#555",
                                color: "#fff",
                                opacity: 0.7
                            }}
                        />
                        
                        <Form.Label className="text-light fw-semibold mb-2">
                            Content
                        </Form.Label>
                        <Form.Text className="d-block mb-3" style={{ color: "#ccc" }}>
                            Edit the extracted content from this web link.
                        </Form.Text>
                        <Form.Control
                            id='content'
                            as="textarea"
                            rows={8}
                            value={text}
                            onChange={(e) => setText(e.target.value)}
                            className="bg-dark text-light border-secondary"
                            style={{ 
                                backgroundColor: "#2d2d2d",
                                borderColor: "#555",
                                color: "#fff",
                                resize: "vertical"
                            }}
                            placeholder="Content extracted from the web link..."
                            disabled={isLoading}
                        />
                    </Form.Group>
                </Form>
                
                {error && (
                    <Alert 
                        variant="danger" 
                        className="mt-3"
                        style={{ 
                            backgroundColor: "#2d1a1a",
                            borderColor: "#dc3545",
                            color: "#ff6b6b"
                        }}
                    >
                        <div className="d-flex align-items-center">
                            <FaExclamationTriangle className="me-2" />
                            {error}
                        </div>
                    </Alert>
                )}
            </Modal.Body>
            
            <Modal.Footer 
                className="border-secondary"
                style={{ backgroundColor: "#2d2d2d" }}
            >
                <Button 
                    variant="outline-secondary" 
                    onClick={handleClose}
                    className="d-flex align-items-center"
                    style={{ borderColor: "#555", color: "#ccc" }}
                    disabled={isLoading}
                >
                    <FaTimes className="me-2" size={14} />
                    Cancel
                </Button>
                <Button 
                    variant="info" 
                    onClick={handleUpdate}
                    disabled={permission || isLoading}
                    className="d-flex align-items-center"
                    style={{ 
                        backgroundColor: "#17a2b8",
                        borderColor: "#17a2b8"
                    }}
                >
                    {isLoading ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Updating...
                        </>
                    ) : (
                        <>
                            <FaSave className="me-2" size={14} />
                            Update Content
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ViewWebLink;
